import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ImageModalData, PageStats } from '@core/models';
import { ImageUploadModalComponent } from '@shared/components/image-upload-modal/image-upload-modal.component';
import { AbstractControl, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { NgClass, UpperCasePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { NameToColorPipe } from '@shared/pipes';
import { MatDialog } from '@angular/material/dialog';
import { StatListComponent } from '@shared/components/stat-list/stat-list.component';
import { RouterLink } from '@angular/router';
import { ProfileStatsConstant, RouterLinks } from '@constants';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-profile-container',
  templateUrl: './profile-container.component.html',
  styleUrls: ['./profile-container.component.scss'],
  imports: [MatIconModule, NameToColorPipe, UpperCasePipe, MatButtonModule],
})
export class ProfileContainerComponent implements OnInit {
  @Input('defaultImage') set defaultImage(data: {
    image: string;
    type: string;
  }) {
    if (!data || !this.profileForm) {
      return;
    }
    const imageURl = data.image ? this._addRandomString(data.image) : null;
    this.profileForm.get('image').setValue(imageURl);
    this.imageType = data.type;
  }

  @Input() isDisabled: boolean;
  @Input() isProfileView = false;
  @Output() save: EventEmitter<{
    image: File;
    image_deleted: boolean;
    cropped?: string;
  }> = new EventEmitter<{ image: File; image_deleted: boolean }>();
  @Output() archive: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() error: string;
  @Input() data: { name: string; isPrivate?: number };
  readonly staffRouterLink = RouterLinks.STAFF;
  private imageReloaded: boolean = false;
  private imageFile = { file: null, name: '', cropped: '' };
  private imageDeleted: boolean = false;
  public profileForm: FormGroup;
  public imageType: string;

  constructor(
    private matDialog: MatDialog,
    private fb: UntypedFormBuilder,
  ) {}

  ngOnInit(): void {
    this._initForm();
  }

  private _initForm(): void {
    this.profileForm = this.fb.group({
      image: '',
    });
  }

  /**
   * Calls image upload modal
   * Sets form field value uploaded url
   * @param field - Image control field
   * @param width - Image width
   * @param height - Image height
   * @param imageType - Image type id
   */
  public openImageModal({
    field,
    height,
    width,
    imageType,
  }: ImageModalData): void {
    const oldSrc = field.value;

    this.matDialog
      .open(ImageUploadModalComponent, {
        data: {
          height,
          width,
          imageType,
          imageUrl: oldSrc,
        },
      })
      .afterClosed()
      .subscribe({
        next: (v) => {
          if (v && v.croppedImage) {
            this.imageFile.file = v.file;
            this.imageFile.name = v.fileName;
            this.imageFile.cropped = v.croppedImage;
            this.setFieldValue(field, v.croppedImage);
            return;
          }
          if (v?.imageDeleted) {
            this.removeImage();
          }
        },
        error: () => {
          field.setValue(oldSrc);
        },
      });
  }

  /**
   * Adds random string to url to prevent browser cashing
   * @param url - Image url
   */
  private _addRandomString(url: string): string {
    return `${url}?cash=${new Date().getTime()}`;
  }

  public removeImage(): void {
    this.profileForm.get('image').setValue(null);
    this.imageFile = { file: null, name: '', cropped: '' };
    this.imageDeleted = true;
  }

  public reloadImage(imageForm: AbstractControl): void {
    if (!this.imageReloaded) {
      const changedImage = `${imageForm.value}?t=${new Date().getTime()}`;
      imageForm.setValue(changedImage);
    }
    this.imageReloaded = true;
  }

  onArchive(): void {
    this.archive.emit(true);
  }

  onSave(): void {
    this.save.emit({
      image: this.imageFile.file,
      image_deleted: this.imageDeleted,
      cropped: this.imageFile.cropped,
    });
    this.imageDeleted = false;
    this.imageReloaded = false;
  }

  private setFieldValue(field: AbstractControl, value: string): void {
    field.setValue(value);
    field.markAsTouched();
  }
}
